/** Atomic publication contracts. Ownership is independent of retry operation identity. */
import { z } from 'zod';
import { macroTriggerSchema } from '../macroTree';
import { macroAuthoringDocumentSchema } from './authoring';
import { savedMacroSchema } from './authoringFlow';
import { canonicalJson, sha256Hex, sha256HexSchema } from './canonicalJson';
import { macroAuthoringSourceGraphSchema, type MacroAuthoringSourceGraph } from './sourceGraph';

const revision = z.number().int().nonnegative().safe();
const nodeIds = z.array(z.string().uuid()).max(4096)
  .refine(ids => new Set(ids).size === ids.length, 'Node identities must be unique');
export const macroPublicationRequestSchema = z.object({ operationId: z.string().uuid(), expectedRev: revision,
  instanceId: z.string().uuid(), document: macroAuthoringDocumentSchema, saved: savedMacroSchema,
  expectedProjectionDigest: sha256HexSchema.nullable(),
}).strict().superRefine((request, context) => {
  const { document, saved } = request;
  if (document.macroId !== saved.macroId || document.revision !== saved.revision
    || saved.source.packages.length !== 1
    || canonicalJson(saved.source.packages[0]) !== canonicalJson(document.recording)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Publication must name the exact saved macro revision and recording pin' });
  }
});
export type MacroPublicationRequest = z.infer<typeof macroPublicationRequestSchema>;

export const macroPublicationResultSchema = z.object({ campaignId: z.string().min(1).max(128), trigger: macroTriggerSchema,
  rev: revision.refine(value => value > 0), instanceId: z.string().uuid(), authoringDigest: sha256HexSchema,
  projectionDigest: sha256HexSchema, addedNodeIds: nodeIds, updatedNodeIds: nodeIds, removedNodeIds: nodeIds,
}).strict().refine(result => {
  const ids = [...result.addedNodeIds, ...result.updatedNodeIds, ...result.removedNodeIds];
  return new Set(ids).size === ids.length;
}, 'Publication changes must be disjoint');
export type MacroPublicationResult = z.infer<typeof macroPublicationResultSchema>;

export const macroPublicationOwnershipSchema = z.object({ instanceId: z.string().uuid(), macroId: z.string().uuid(),
  revision: revision.refine(value => value > 0), authoringDigest: sha256HexSchema, projectionDigest: sha256HexSchema,
  nodeIds: nodeIds.refine(ids => ids.length > 0),
}).strict();
export type MacroPublicationOwnership = z.infer<typeof macroPublicationOwnershipSchema>;

/** Schemas check cross-field identities synchronously; digest verification requires WebCrypto. */
export async function validateMacroPublicationIdentity(request: MacroPublicationRequest): Promise<{ ok: boolean; issues: string[] }> {
  const parsed = macroPublicationRequestSchema.safeParse(request);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues.map(issue => issue.message) };
  const issues: string[] = [];
  if (await sha256Hex(canonicalJson(parsed.data.document)) !== parsed.data.saved.authoringDigest) {
    issues.push('publication_authoring_digest_mismatch');
  }
  const { document, saved } = parsed.data;
  if (saved.source.metadata.authoringDigest !== undefined
    && saved.source.metadata.authoringDigest !== saved.authoringDigest) issues.push('publication_source_authoring_digest_mismatch');
  if (canonicalJson(document.policy) !== canonicalJson(saved.source.policy)) issues.push('publication_input_policy_mismatch');
  if (saved.source.nodes.some(node => node.input && (node.input.fromUs < document.range.fromUs
    || node.input.toUs > document.range.toUs))) issues.push('publication_input_range_mismatch');
  const regions = (values: unknown[]): string => canonicalJson(values.map(region => canonicalJson(region)).sort());
  if (regions(document.groups.flatMap(group => group.regions))
    !== regions(saved.source.nodes.flatMap(node => node.expectedState?.regions ?? []))) {
    issues.push('publication_authored_regions_mismatch');
  }
  return { ok: issues.length === 0, issues };
}

/**
 * Freeze the owned projection proof for services and atomic storage. Include every owned
 * node field (including layout), internal edge, referenced recording pin and global input
 * policy. Exclude external edges and unrelated nodes so edits to other instances are safe.
 * Layout edits to owned nodes deliberately conflict rather than silently overwrite them.
 */
export function macroPublicationProjectionBody(graph: MacroAuthoringSourceGraph,
  instanceId: string, macroId: string, ownedNodeIds: readonly string[]): unknown {
  const source = macroAuthoringSourceGraphSchema.parse(graph);
  const owner = { instanceId: z.string().uuid().parse(instanceId), macroId: z.string().uuid().parse(macroId) };
  const ids = nodeIds.parse([...ownedNodeIds]);
  const owned = new Set(ids);
  const compare = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0;
  const nodes = source.nodes.filter(node => owned.has(node.id)).sort((a, b) => compare(a.id, b.id));
  if (!ids.length || nodes.length !== ids.length) throw new Error('publication_owned_projection_missing');
  const packages = new Set(nodes.flatMap(node => [
    ...(node.input ? [node.input.packageId] : []), ...(node.expectedState?.regions.map(region => region.packageId) ?? []),
  ]));
  return { version: 1, campaignId: source.campaignId, trigger: source.trigger, ...owner,
    nodes, edges: source.edges.filter(edge => owned.has(edge.source) && owned.has(edge.target)).sort((a, b) => compare(a.id, b.id)),
    packages: source.packages.filter(pin => packages.has(pin.packageId)).sort((a, b) => compare(a.packageId, b.packageId)),
    policy: source.policy };
}
export async function macroPublicationProjectionDigest(graph: MacroAuthoringSourceGraph,
  instanceId: string, macroId: string, ownedNodeIds: readonly string[]): Promise<string> {
  return sha256Hex(canonicalJson(macroPublicationProjectionBody(graph, instanceId, macroId, ownedNodeIds)));
}
